import {
  SkyBreadcrumb,
  SkyBreadcrumbButton,
  SkyBreadcrumbDivider,
  SkyBreadcrumbItem,
} from "@skypointcloud/skypoint-ui";
import { Fragment, type ReactNode } from "react";
import { useLocation, useRouter } from "wouter";
import { usePageCrumbLabel } from "@/components/shell/PageCrumb";
import { useTenantInstance } from "@/contexts/TenantInstanceContext";
import { breadcrumbTrail } from "@/lib/routes";

/**
 * The breadcrumb row above the page header (SKILL "Breadcrumbs are mandatory"; the template's
 * `.breadcrumbs`: small type, divider under, current page bold and last).
 *
 * Rendered once from `App.tsx`, read from the route table (`breadcrumbTrail`), never by a page,
 * on every screen. The run, schedule and transformation details name their record here through
 * `PageCrumb` (post-release 607e63a: they used to draw their own trail under the page header,
 * and this row was missing there). `min-h-11` plus the border is the nav drawer's header row
 * (a 28 px toggle in `py-2` plus its border), so the two dividers meet.
 *
 * A link back is a real `<a>` with its href (middle-click and copy-link work) whose left click
 * is routed: a document load would re-enter the app before the dev token is read and land on
 * sign-in (the same reason `runs/detail.tsx` routes its breadcrumb click).
 */
export function ShellBreadcrumb() {
  const [location, navigate] = useLocation();
  // #1220: the link's address carries the instance, as the side nav's does (`InstanceRouter`).
  const { hrefs } = useRouter();
  const features = useTenantInstance().me?.features;
  const trail = breadcrumbTrail(location, usePageCrumbLabel(), features ?? {});

  return (
    <div
      className="shrink-0 border-b border-border bg-background"
      data-testid="shell-breadcrumb-row"
    >
      <div className="flex min-h-11 items-center px-4">
        <SkyBreadcrumb size="small" testId="shell-breadcrumb">
          {trail.map((crumb, index) => {
            const last = index === trail.length - 1;
            function renderCrumb(): ReactNode {
              if (last) {
                return (
                  <SkyBreadcrumbButton size="small" current>
                    {crumb.label}
                  </SkyBreadcrumbButton>
                );
              }
              if (crumb.href) {
                return (
                  <SkyBreadcrumbButton
                    size="small"
                    href={hrefs(crumb.href)}
                    onClick={(event) => {
                      event.preventDefault();
                      if (crumb.href) navigate(crumb.href);
                    }}
                  >
                    {crumb.label}
                  </SkyBreadcrumbButton>
                );
              }
              return (
                <span className="px-1 py-1 text-xs font-medium text-muted-foreground">
                  {crumb.label}
                </span>
              );
            }

            return (
              <Fragment key={`${crumb.label}-${index}`}>
                <SkyBreadcrumbItem>{renderCrumb()}</SkyBreadcrumbItem>
                {last ? null : <SkyBreadcrumbDivider />}
              </Fragment>
            );
          })}
        </SkyBreadcrumb>
      </div>
    </div>
  );
}
